import React, { useEffect, useState } from 'react';
import { Cpu, Play, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import apiService from '../services/api';

interface EngineOption {
  value: string;
  label: string;
  description: string;
}

interface OptimizeResponse {
  status: string;
  engine: string;
  flights_total: number;
  flights_covered: number;
  flights_uncovered: number;
  assignments_created: number;
  crew_with_duty: number;
  duty_hours_min: number;
  duty_hours_max: number;
  duty_hours_mean: number;
  solve_time_s: number;
  objective_value: number | null;
  written: number;
  uncovered_sample: Array<{
    flight_id: number;
    flight_number: string;
    op_date: string;
    role: string;
    missing: number;
  }>;
}

const Optimizer: React.FC = () => {
  const [engines, setEngines] = useState<EngineOption[]>([]);
  const [engine, setEngine] = useState('coverage');
  const [startDate, setStartDate] = useState('2026-05-01');
  const [endDate, setEndDate] = useState('2026-05-07');
  const [timeLimit, setTimeLimit] = useState(60);
  const [clean, setClean] = useState(true);
  const [write, setWrite] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OptimizeResponse | null>(null);

  useEffect(() => {
    apiService.client
      .get('/optimizer/engines')
      .then((r) => setEngines(r.data.engines || []))
      .catch(() => setEngines([]));
  }, []);

  const handleRun = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const response = await apiService.client.post<OptimizeResponse>('/optimizer/run', {
        start_date: startDate,
        end_date: endDate,
        engine,
        time_limit_s: timeLimit,
        clean,
        write,
      });
      setResult(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Cpu className="h-8 w-8 text-sky-400" />
        <div>
          <h1 className="text-3xl font-bold text-white">Roster Optimizer</h1>
          <p className="text-sm text-gray-400">
            Solve crew assignments with OR-Tools CP-SAT under OTAR FDP constraints
          </p>
        </div>
      </div>

      <div className="glass-card p-6 space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Start date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-white focus:border-sky-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">End date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-white focus:border-sky-500 focus:outline-none"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Engine</label>
          <select
            value={engine}
            onChange={(e) => setEngine(e.target.value)}
            className="w-full rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-white focus:border-sky-500 focus:outline-none"
          >
            {engines.length === 0 && <option value="coverage">Coverage only</option>}
            {engines.map((e) => (
              <option key={e.value} value={e.value}>
                {e.label}
              </option>
            ))}
          </select>
          {engines.find((e) => e.value === engine)?.description && (
            <p className="mt-1 text-xs text-gray-500">{engines.find((e) => e.value === engine)?.description}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Solver time limit: {timeLimit}s
          </label>
          <input
            type="range"
            min={10}
            max={300}
            step={10}
            value={timeLimit}
            onChange={(e) => setTimeLimit(Number(e.target.value))}
            className="w-full"
          />
        </div>

        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={clean}
              onChange={(e) => setClean(e.target.checked)}
              className="h-4 w-4 rounded border-gray-700 bg-gray-800 text-sky-500"
            />
            Wipe existing assignments in this range first
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={write}
              onChange={(e) => setWrite(e.target.checked)}
              className="h-4 w-4 rounded border-gray-700 bg-gray-800 text-sky-500"
            />
            Write results to database
          </label>
        </div>

        <div>
          <button
            onClick={handleRun}
            disabled={running}
            className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-sky-500 to-sky-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-sky-500/30 transition hover:from-sky-400 hover:to-sky-500 disabled:opacity-50"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            {running ? 'Solving…' : 'Run optimizer'}
          </button>
        </div>
      </div>

      {error && (
        <div className="glass-card border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300 flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Run failed</p>
            <p className="text-red-400/80">{error}</p>
          </div>
        </div>
      )}

      {result && (
        <div className="space-y-4">
          <div className="glass-card p-4 flex items-center gap-3">
            {result.status === 'OPTIMAL' || result.status === 'FEASIBLE' ? (
              <CheckCircle2 className="h-6 w-6 text-emerald-400" />
            ) : (
              <AlertTriangle className="h-6 w-6 text-amber-400" />
            )}
            <div>
              <p className="text-sm text-gray-400">Solver status</p>
              <p className="text-lg font-semibold text-white">
                {result.status} · engine: {result.engine} · {result.solve_time_s.toFixed(1)}s
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Flights total" value={result.flights_total} />
            <Stat label="Flights covered" value={result.flights_covered} accent="emerald" />
            <Stat label="Flights uncovered" value={result.flights_uncovered} accent={result.flights_uncovered > 0 ? 'amber' : undefined} />
            <Stat label="Assignments" value={result.assignments_created} />
            <Stat label="Crew used" value={result.crew_with_duty} />
            <Stat label="Min duty hrs" value={result.duty_hours_min.toFixed(1)} />
            <Stat label="Mean duty hrs" value={result.duty_hours_mean.toFixed(1)} />
            <Stat label="Max duty hrs" value={result.duty_hours_max.toFixed(1)} />
          </div>

          <div className="glass-card p-4">
            <p className="text-sm text-gray-400">Persisted to database</p>
            <p className="text-2xl font-bold text-white">{result.written.toLocaleString()} rows</p>
          </div>

          {result.uncovered_sample.length > 0 && (
            <div className="glass-card p-4">
              <h3 className="text-sm font-medium text-amber-400 mb-2">Uncovered slots (sample)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-400 border-b border-gray-800">
                      <th className="text-left py-2 pr-4">Flight</th>
                      <th className="text-left py-2 pr-4">Date</th>
                      <th className="text-left py-2 pr-4">Role</th>
                      <th className="text-left py-2">Missing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.uncovered_sample.map((u) => (
                      <tr key={`${u.flight_id}-${u.role}`} className="border-b border-gray-800/50 text-gray-200">
                        <td className="py-2 pr-4">{u.flight_number}</td>
                        <td className="py-2 pr-4">{u.op_date}</td>
                        <td className="py-2 pr-4">{u.role}</td>
                        <td className="py-2">{u.missing}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: number | string; accent?: 'emerald' | 'amber' }> = ({
  label,
  value,
  accent,
}) => (
  <div className="glass-card p-3">
    <p className="text-xs text-gray-400">{label}</p>
    <p
      className={`mt-1 text-2xl font-bold ${
        accent === 'emerald' ? 'text-emerald-400' : accent === 'amber' ? 'text-amber-400' : 'text-white'
      }`}
    >
      {value}
    </p>
  </div>
);

export default Optimizer;
